import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PiArrowLeftDuotone,
  PiTagDuotone,
  PiPencilSimpleDuotone,
  PiEyeDuotone,
} from 'react-icons/pi';
import { listAllEventTypes } from '../api/eventTypeService';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Badge from '../shared/common/Badge';
import EmptyState from '../shared/common/EmptyState';
import { SkeletonCard } from '../shared/common/Skeleton';
import { outcomeSchemeLabel, outcomeSchemeIcon } from '../shared/business/eventType/outcomeSchemeHelpers';

const LOAD_ERROR = 'Не удалось загрузить типы событий';

export default function EventTypeListPage() {
  const navigate = useNavigate();
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await listAllEventTypes();
        setEventTypes(Array.isArray(data) ? data : data?.content ?? []);
      } catch {
        setError(LOAD_ERROR);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="bm-animate-in" style={{ maxWidth: 720 }}>
      <Button
        variant="ghost"
        size="sm"
        icon={<PiArrowLeftDuotone size={16} />}
        onClick={() => navigate('/dashboard')}
        style={{ marginBottom: 'var(--bm-space-lg)' }}
      >
        На главную
      </Button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--bm-space-xl)' }}>
        <h1 className="bm-h2">Типы событий</h1>
        <Button
          variant="primary"
          size="sm"
          icon={<PiTagDuotone size={16} />}
          onClick={() => navigate('/event-types/new')}
        >
          Новый тип
        </Button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : error ? (
        <EmptyState title="Ошибка" description={error} />
      ) : eventTypes.length === 0 ? (
        <EmptyState
          title="Нет типов событий"
          description="Создайте первый тип события для настройки схем исходов."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-sm)' }}>
          {eventTypes.map((eventType) => (
            <EventTypeCard
              key={eventType.id}
              eventType={eventType}
              onView={() => navigate(`/event-types/${eventType.id}`)}
              onEdit={eventType.isBuiltIn ? null : () => navigate(`/event-types/${eventType.id}/edit`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EventTypeCard({ eventType, onView, onEdit }) {
  const schemeLabel = outcomeSchemeLabel(eventType.outcomeScheme);
  const schemeIcon = outcomeSchemeIcon(eventType.outcomeScheme);

  return (
    <Card style={{ padding: 'var(--bm-space-md)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--bm-space-sm)', marginBottom: 'var(--bm-space-xs)' }}>
            <span className="bm-text-lg bm-text-bold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {eventType.name}
            </span>
            {eventType.isBuiltIn && <Badge variant="secondary">Встроенный</Badge>}
          </div>
          <div className="bm-muted bm-text-sm">
            {schemeIcon} {schemeLabel}
            {eventType.slug && <span style={{ marginLeft: 'var(--bm-space-sm)' }}>· @{eventType.slug}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--bm-space-xs)' }}>
          <Button variant="ghost" size="sm" icon={<PiEyeDuotone size={16} />} onClick={onView}>
            Просмотр
          </Button>
          {onEdit && (
            <Button variant="ghost" size="sm" icon={<PiPencilSimpleDuotone size={16} />} onClick={onEdit}>
              Редактировать
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
